import { FC, useEffect } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { WeaveInsuranceEligibilityIllustration } from '@frontend/self-serve#svg';
import { pendo } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { Text, Button } from '@frontend/design-system';
import { EIVDashboardTrialWidgetTrackingIds } from '../../../../constants/tracking-ids';
import { WidgetLayout } from './widget-layout';

interface TrialEligibleWidgetProps {
  containerStyles?: Interpolation<Theme>;
  disableDismiss?: boolean;
  onStartTrialClick?: () => void;
  onUpgradeClick?: () => void;
  onDismissClick?: () => void;
}

export const TrialEligibleWidget: FC<TrialEligibleWidgetProps> = ({
  containerStyles,
  disableDismiss,
  onStartTrialClick,
  onUpgradeClick,
  onDismissClick,
}) => {
  const { t } = useTranslation('featurePromotion', { keyPrefix: 'weave-insurance-eligibility' });

  const { ref, inView } = useInView({
    triggerOnce: true,
  });
  useEffect(() => {
    if (inView) {
      pendo.track(EIVDashboardTrialWidgetTrackingIds.trialEligibleWidgetVisible);
    }
  }, [inView]);

  return (
    <WidgetLayout containerStyles={containerStyles} innerRef={ref}>
      <div css={defaultStyles.body}>
        <div css={defaultStyles.content}>
          <Text weight='bold' size='large' css={{ gridArea: 'title' }}>
            {t(
              'Reduce claim denials and simplify your workflow with detailed, accurate insurance eligibility.  Start your 30 day free trial now.'
            )}
          </Text>
          <Text size='medium' css={{ gridArea: 'subtitle' }}>
            {t('Activate your 30 day Free Trial today. No commitment. No credit card required.')}
          </Text>
          <div css={defaultStyles.actions}>
            <Button
              variant='primary'
              onClick={onStartTrialClick}
              trackingId={EIVDashboardTrialWidgetTrackingIds.startTrialAction}
            >
              {t('Start Free Trial')}
            </Button>
            <Button
              variant='secondary'
              onClick={onUpgradeClick}
              trackingId={EIVDashboardTrialWidgetTrackingIds.upgradeAction}
            >
              {t('Upgrade Now')}
            </Button>
            <Button
              variant='tertiary'
              onClick={onDismissClick}
              disabled={disableDismiss}
              css={{ color: theme.colors.neutral50 }}
              trackingId={EIVDashboardTrialWidgetTrackingIds.dismissAction}
            >
              {t('Maybe Later')}
            </Button>
          </div>
        </div>
        <WeaveInsuranceEligibilityIllustration css={defaultStyles.illustration} />
      </div>
    </WidgetLayout>
  );
};

const defaultStyles = {
  body: css`
    display: flex;
    justify-content: space-between;
    gap: ${theme.spacing(2)};
  `,
  content: css`
    display: grid;
    grid-template-areas:
      'title'
      'subtitle'
      'actions';
    grid-template-rows: auto auto auto;
    grid-template-columns: 1fr;
    gap: ${theme.spacing(2)};
    max-width: 512px;
  `,
  actions: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};

    @media (max-width: ${breakpoints.small.max}px) {
      flex-direction: column;
      align-items: stretch;
    }
  `,
  illustration: css`
    position: absolute;
    scale: 1.06; // adjust scale as image has extra padding
    width: 352px;
    height: auto;
    top: calc(50% + 8px);
    right: 16px;
    transform: translateY(-50%);
    @container (max-width: ${breakpoints.medium.max}px) {
      display: none;
    }
  `,
};
